import type {
  WorldRoomDirectory,
  WorldRoomProjection,
  WorldSessionProjection,
} from "@cleo/contracts";

import type { RoomRailParkedRoom } from "./projectRoomRailGroups";

/**
 * What the Desk keeps out of sight by default (the principal's decision of
 * 6 Oct 2026, "yes to all three": hide archived rooms and retired sessions
 * behind a toggle). The World ledger never deletes anything, so these are
 * display choices only: every record stays addressable by id, a search still
 * finds it, and the rail always says how many it is not showing.
 */

/** A record's state words, as the Desk shows them wherever it opens or finds one. */
export type WorldRecordState = "retired" | "archived-room";

export const WORLD_RECORD_STATE_LABEL: Readonly<Record<WorldRecordState, string>> = {
  retired: "Retired",
  "archived-room": "Archived room",
};

export function worldSessionStates(
  session: Pick<WorldSessionProjection, "lifecycle">,
  room: Pick<WorldRoomProjection, "archived"> | undefined,
): ReadonlyArray<WorldRecordState> {
  const states: WorldRecordState[] = [];
  if (session.lifecycle === "retired") states.push("retired");
  if (room?.archived === true) states.push("archived-room");
  return states;
}

export interface ArchivedRoomEntry {
  readonly room: WorldRoomProjection;
  readonly sessionCount: number;
  /** A search's hits among the room's open sessions, any depth, newest first: each opens itself. */
  readonly matches?: ReadonlyArray<WorldSessionProjection>;
}

/** Every archived room in the World, most recently archived first. */
export function archivedRoomEntries(index: WorldRoomDirectory): ReadonlyArray<ArchivedRoomEntry> {
  const counts = new Map<string, number>();
  for (const session of index.sessions)
    counts.set(session.roomId, (counts.get(session.roomId) ?? 0) + 1);
  return index.rooms
    .filter((room) => room.archived === true)
    .toSorted((a, b) => (b.archiveSeq ?? 0) - (a.archiveSeq ?? 0))
    .map((room) => ({ room, sessionCount: counts.get(room.roomId) ?? 0 }));
}

/**
 * The retired sessions of archived rooms, in the same shape and order as the
 * rail's projection gives its live rooms' (rooms in born order, each room's
 * newest first). The projection leaves archived rooms out, so without these a
 * retired session in an archived room (the standing room included) would be
 * missing from Show retired, from its count and from the addressed row.
 */
export function archivedParkedRooms(index: WorldRoomDirectory): ReadonlyArray<RoomRailParkedRoom> {
  return index.rooms
    .filter((room) => room.archived === true)
    .toSorted((a, b) => a.bornSeq - b.bornSeq)
    .map((room) => ({
      room,
      sessions: index.sessions
        .filter((session) => session.roomId === room.roomId && session.lifecycle === "retired")
        .toSorted((a, b) => b.bornSeq - a.bornSeq),
    }))
    .filter((entry) => entry.sessions.length > 0);
}

/** How many retired sessions the rooms' Retired groups hold. */
export function retiredSessionCount(parked: ReadonlyArray<RoomRailParkedRoom>): number {
  return parked.reduce((total, entry) => total + entry.sessions.length, 0);
}

/**
 * With Show retired off, the retired sessions on the addressed lineage keep
 * their rows (the Local rail's rule, railNavigationModel.ts): World entry, the
 * Workroom door, a link or the palette can land on a retired session, and the
 * session he is on never disappears from the rail.
 */
export function addressedParkedRooms(
  parked: ReadonlyArray<RoomRailParkedRoom>,
  addressed: ReadonlySet<string>,
): ReadonlyArray<RoomRailParkedRoom> {
  return parked
    .map((entry) => ({
      ...entry,
      sessions: entry.sessions.filter((session) => addressed.has(session.sessionId)),
    }))
    .filter((entry) => entry.sessions.length > 0);
}

/**
 * A search reaches retired sessions whether or not they are shown: each room
 * keeps only its retired sessions whose name matches.
 */
export function searchParkedRooms(
  parked: ReadonlyArray<RoomRailParkedRoom>,
  needle: string,
): ReadonlyArray<RoomRailParkedRoom> {
  if (needle.length === 0) return parked;
  return parked
    .map((entry) => ({
      ...entry,
      sessions: entry.sessions.filter((session) =>
        session.label.toLocaleLowerCase().includes(needle),
      ),
    }))
    .filter((entry) => entry.sessions.length > 0);
}

/**
 * A search reaches archived rooms whether or not they are shown: by the
 * room's own name or the name of any open session in it, at any depth. A room
 * found by a session's name carries that session as a match, so the result
 * opens the session that matched, as the Retired group and the palette do,
 * not the room's newest root. A retired session is the Retired group's to
 * list (archivedParkedRooms), so it is not listed here a second time.
 */
export function searchArchivedRooms(
  entries: ReadonlyArray<ArchivedRoomEntry>,
  index: WorldRoomDirectory,
  needle: string,
): ReadonlyArray<ArchivedRoomEntry> {
  if (needle.length === 0) return entries;
  return entries.flatMap((entry) => {
    const matches = index.sessions
      .filter(
        (session) =>
          session.roomId === entry.room.roomId &&
          session.lifecycle !== "retired" &&
          session.label.toLocaleLowerCase().includes(needle),
      )
      .toSorted((a, b) => b.bornSeq - a.bornSeq);
    return matches.length > 0 || entry.room.label.toLocaleLowerCase().includes(needle)
      ? [{ ...entry, matches }]
      : [];
  });
}
